import type { ChartData, ScatterDataPoint } from "chart.js";
import type { LegacyLineDataset, MeasurementChartModel } from "../types";

function camelizeValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(camelizeValue);
  }

  if (!value || typeof value !== "object") {
    return value;
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [
      key.charAt(0).toLocaleLowerCase() + key.slice(1),
      camelizeValue(item)
    ])
  );
}

function unwrapChartsPayload(payload: unknown): unknown {
  let current = payload;

  for (let i = 0; i < 4; i += 1) {
    if (typeof current === "string") {
      const trimmed = current.trim();
      if (!trimmed.startsWith("[") && !trimmed.startsWith("{") && !trimmed.startsWith('"')) {
        break;
      }

      try {
        current = JSON.parse(trimmed) as unknown;
        continue;
      } catch {
        break;
      }
    }

    if (!current || typeof current !== "object" || Array.isArray(current)) {
      break;
    }

    const record = current as Record<string, unknown>;
    if ("result" in record || "Result" in record) {
      current = record.result ?? record.Result;
      continue;
    }

    if ("charts" in record || "Charts" in record) {
      current = record.charts ?? record.Charts;
      continue;
    }

    break;
  }

  return current;
}

function isMeasurementChartModel(value: unknown): value is MeasurementChartModel {
  if (!value || typeof value !== "object") {
    return false;
  }

  const chart = value as Partial<MeasurementChartModel>;
  return typeof chart.chartName === "string" && !!chart.data && !!chart.options;
}

/**
 * Accepts both the MVC array response and common API envelopes/casing variants.
 * Null means the response had an unsupported shape; an empty array is a valid
 * response saying that the profile currently has no charts.
 */
export function normalizeMeasurementChartsPayload(payload: unknown): MeasurementChartModel[] | null {
  const unwrapped = unwrapChartsPayload(payload);
  if (!Array.isArray(unwrapped)) {
    return null;
  }

  const normalized = unwrapped.map(camelizeValue).filter(isMeasurementChartModel);
  if (unwrapped.length > 0 && normalized.length === 0) {
    return null;
  }

  return normalized;
}

function camelizeRecord(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => [key.charAt(0).toLocaleLowerCase() + key.slice(1), item])
  );
}

export function normalizeLegacyDatasets(datasets: unknown): LegacyLineDataset[] {
  if (!Array.isArray(datasets)) {
    return [];
  }

  return datasets.map((dataset) => {
    const normalized = camelizeRecord(dataset);
    const data = Array.isArray(normalized.data)
      ? normalized.data.map((point) => {
          const normalizedPoint = camelizeRecord(point);
          return {
            ...normalizedPoint,
            metadata: camelizeRecord(normalizedPoint.metadata)
          };
        })
      : [];

    return { ...normalized, data } as unknown as LegacyLineDataset;
  });
}

export function convertDataset(dataset: LegacyLineDataset): Record<string, unknown> {
  return {
    ...dataset,
    tension: dataset.tension ?? dataset.lineTension ?? 0,
    stepped: dataset.steppedLine,
    parsing: false
  };
}

export function convertLegacyData(
  datasets: LegacyLineDataset[] | undefined
): ChartData<"scatter", ScatterDataPoint[]> {
  const normalized = normalizeLegacyDatasets(datasets);
  return {
    datasets: normalized.map((dataset) => convertDataset(dataset) as never)
  };
}
